/**
 * buildPayload — pure async function that maps form state to the
 * POST /api/v1/blocks wire payload.
 *
 * Extracted to its own file so it can be unit-tested without violating
 * the react-refresh/only-export-components lint rule (Request.tsx may
 * only export the React component).
 *
 * Backend contract (2026-06-15):
 *   block_type  : required, matches BlockType
 *   target fields vary by block_type:
 *     account   → target_nuban + target_bank_code
 *     customer  → target_customer_public_id
 *     bvn | card | device | ip | terminal → target_value
 *   Obsolete keys (type, target_identifier, bvn_hash, is_regulatory) MUST
 *   NOT be sent — backend rejects unknown fields and emits "invalid block_type """.
 */
import { hmacSha256Hex } from '@/lib/hmac'
import type { BVNInputHandle } from './components/BVNInput'
import type { BlockSource, BlockType, RequestBlockPayload } from './types'

export interface BuildPayloadArgs {
  type: BlockType
  account: { bankCode: string | null; nuban: string }
  identifier: string
  reason: string
  source: BlockSource
  sourceReference: string
  effectiveFrom: string
  effectiveTo: string
  isRegulatory: boolean
  bvnRef: React.RefObject<BVNInputHandle | null>
  pepper: string | null
}

export async function buildPayload(args: BuildPayloadArgs): Promise<RequestBlockPayload> {
  // Common fields present on every payload shape.
  // Regulatory intent is expressed through `source`; no separate is_regulatory flag.
  const common = {
    reason: args.reason.trim(),
    source: args.source,
    source_reference: args.sourceReference || undefined,
    effective_from: new Date(args.effectiveFrom).toISOString(),
    effective_to: args.effectiveTo ? new Date(args.effectiveTo).toISOString() : undefined,
  } as const

  if (args.type === 'account') {
    if (!args.account.bankCode || !args.account.nuban) {
      throw new Error('Bank and NUBAN are required for an account block.')
    }
    return {
      ...common,
      block_type: 'account',
      target_nuban: args.account.nuban,
      target_bank_code: args.account.bankCode,
    }
  }

  if (args.type === 'customer') {
    return {
      ...common,
      block_type: 'customer',
      target_customer_public_id: args.identifier.trim(),
    }
  }

  if (args.type === 'bvn') {
    const raw = args.bvnRef.current?.readRaw()
    if (!raw) throw new Error('BVN must be 11 digits.')
    if (!args.pepper) throw new Error('Session pepper unavailable; reload and retry.')
    // HMAC-SHA256(pepper, raw_bvn) — raw BVN never crosses the wire.
    const hashed = await hmacSha256Hex(args.pepper, raw)
    return {
      ...common,
      block_type: 'bvn',
      target_value: hashed,
    }
  }

  // card | device | ip | terminal
  return {
    ...common,
    block_type: args.type,
    target_value: args.identifier.trim(),
  }
}
